import { memo, useEffect, useRef } from "react";
import { Loader } from "shared/components/Loader";
import { Show } from "shared/components";
import { NoRowsFallback } from "shared/components/DataGridTable/slices/NoRowsFallback";
import { PlazaIcon } from "@hugeicons-pro/core-stroke-rounded";
import { Unit } from "types/landlord/address";
import type { IconSvgElement } from "@hugeicons/react";
import { UnitCard } from "./UnitCard";

const MemoUnitCard = memo(UnitCard);

export const UnitsCardGrid = ({
  units,
  isLoading,
  emptyTitle,
  emptyText,
  emptyIcon = PlazaIcon,
  onActivityClick,
  visibleFields,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore
}: {
  units: Unit[];
  isLoading: boolean;
  emptyTitle: string;
  emptyText: string;
  emptyIcon?: IconSvgElement;
  onActivityClick: (unit: Unit) => void;
  visibleFields: Set<string>;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onLoadMore: () => void;
}) => {
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasNextPage) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) onLoadMore();
      },
      { rootMargin: "400px" }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [hasNextPage, onLoadMore]);

  if (isLoading)
    return (
      <div className="flex justify-center py-16">
        <Loader />
      </div>
    );

  if (!units.length)
    return (
      <NoRowsFallback Icon={emptyIcon} title={emptyTitle} text={emptyText} />
    );

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {units.map((unit) => (
          <MemoUnitCard
            key={unit.id}
            unit={unit}
            onActivityClick={onActivityClick}
            visibleFields={visibleFields}
          />
        ))}
      </div>
      <div ref={sentinelRef} aria-hidden className="h-px" />

      <Show if={isFetchingNextPage}>
        <div className="flex justify-center py-6">
          <Loader />
        </div>
      </Show>
    </div>
  );
};
